import { Cohort, ErrorCode } from '@afterpaytouch/portal-api/types'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { parseCookies } from 'nookies'
import {
  CheckoutAppState,
  checkoutInitialDataSelector,
  checkoutSelector,
  checkoutStatusSelector,
  consumerSelector,
  convergenceCohortSelector,
  isRecoverableFlow,
  mobileLoginSelector,
  oAuth2faHeaders,
  portalAction,
  PortalThunk,
  PortalThunkConfig,
  profilingIdSelector,
  recoverableTerminalErrorsStateSelector,
  tokenSelector,
} from '../../'
import {
  AFTERPAY_CHECKOUT_LOGIN_TOKEN,
  APPLE_ORDER_SOURCES,
  AuthLoginRequest,
  AuthLoginResponse,
  CASH_CHECKOUT_TOKEN,
  CheckoutStatus,
  CheckoutTokenResponse,
  CHECKOUT_TOKEN_REQUEST,
  PaymentMethodType,
  PersistentLoginHeaders,
  SendResetLinkRequest,
  VerificationMethod,
  EFA_CHECK_STATUS,
  OAuth2faHeaders,
} from '../../../model'
import { createFingerPrint } from '../../../utils'
import { isAfterpayInAppBrowser, isCashAppInAppBrowser } from '../../../utils/device'
import { FLOW_COOKIE_NAME, FlowCookieValue } from '../../../utils/server/flowCookie'
import { hasOptedOutRememberMe } from '../../../flow/remember-me/optOut'
import { afterpayCheckoutLoginTokenSelector, authenticationSelector, cashCheckoutTokenSelector, isTrustedCheckoutSelector } from './hooks'
import jwtDecode from 'jwt-decode'
import { isCallable } from '@afterpay/utils'
import { RecoverableTerminalErrorStatus } from '@afterpay/types'

export const createFingerprint = createAsyncThunk('user/fingerprint', async () => await createFingerPrint())

const isApplePaylaterFlow = (state: CheckoutAppState): boolean => {
  const flowCookie = parseCookies()[FLOW_COOKIE_NAME] as FlowCookieValue | undefined
  const source = checkoutSelector(state).checkout?.merchant?.source
  return flowCookie === FlowCookieValue.ApplePaylater && source != null && APPLE_ORDER_SOURCES.includes(source)
}

const isFreshLoginSurface = (state: CheckoutAppState): boolean =>
  !isRecoverableFlow(recoverableTerminalErrorsStateSelector(state)?.status ?? RecoverableTerminalErrorStatus.NotStarted) &&
  checkoutStatusSelector(state) < CheckoutStatus.Starting

const rememberMeStepUpLoginParam = (state: CheckoutAppState): boolean =>
  convergenceCohortSelector(state) === Cohort.APOnly &&
  !authenticationSelector(state).persistent &&
  !isTrustedCheckoutSelector(state) &&
  checkoutInitialDataSelector(state)?.isIframe !== true &&
  isFreshLoginSurface(state) &&
  !isApplePaylaterFlow(state) &&
  !isAfterpayInAppBrowser() &&
  !isCashAppInAppBrowser() &&
  !hasOptedOutRememberMe()

export const authLogin: PortalThunk<AuthLoginRequest, AuthLoginResponse> = portalAction('login', async (request, thunk) => {
  const state = thunk.getState()
  const headers: OAuth2faHeaders = {
    ...oAuth2faHeaders(state),
  }

  const { username, password } = request
  const formData = new URLSearchParams({
    username,
    password,
    'remember-me': 'false',
    'remember-me-step-up': String(rememberMeStepUpLoginParam(state)),
  })
  return await thunk.extra.portalApi().post('/portal/consumers/auth/login', formData, headers)
})

export const authLogout: PortalThunk<void, void> = portalAction('logout', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/auth/logout', {})
})

export type PersistentStepUpReason = 'absolute-cap' | 'risk'

export interface PersistentLoginResult {
  active: boolean
  verify: boolean
  isTrustedCheckout: boolean
  allowEmail2Fa?: boolean
  stepUpReason?: PersistentStepUpReason
  maskedMobile?: string
  givenNames?: string
}

export const checkPersistentLogin: PortalThunk<void, PersistentLoginResult> = portalAction('consumer/checkPersistentLogin', async (_, thunk) => {
  try {
    const state = thunk.getState()
    const transactionToken = tokenSelector(state)
    const afterpayCheckoutLoginToken = afterpayCheckoutLoginTokenSelector(state) ?? null
    const cashCheckoutToken = cashCheckoutTokenSelector(state)
    const headers: PersistentLoginHeaders = {
      ...(transactionToken !== '' && { Authorization: `Bearer ${transactionToken}` }),
      ...oAuth2faHeaders(state),
      ...(cashCheckoutToken != null && { 'x-cash-token': cashCheckoutToken }),
      ...(afterpayCheckoutLoginToken != null && { 'x-afterpay-colt': afterpayCheckoutLoginToken }),
    }
    const config = { headers, validateStatus: () => true }
    const {
      status,
      data,
      headers: responseHeaders,
    } = await thunk.extra.portalApi().axiosInstance.post('/portal/consumers/persistent-login/enforce', undefined, config)
    const isTrustedCheckout = responseHeaders?.['x-is-trusted-checkout'] === 'true'
    const allowEmail2Fa = responseHeaders?.['x-2fa-allow-email'] === 'true'
    const stepUpReasonHeader = responseHeaders?.['x-step-up-reason']
    const stepUpReason = stepUpReasonHeader === 'absolute-cap' || stepUpReasonHeader === 'risk' ? stepUpReasonHeader : undefined
    const maskedMobile = status === 207 && typeof data?.mobile === 'string' && data.mobile !== '' ? data.mobile : undefined
    const givenNames = status === 207 && typeof data?.givenNames === 'string' && data.givenNames.trim() !== '' ? data.givenNames : undefined
    return {
      active: Math.floor(status / 100) === 2,
      verify: status === 207,
      allowEmail2Fa,
      isTrustedCheckout,
      stepUpReason,
      maskedMobile,
      givenNames,
    }
  } catch (e) {
    return {
      active: false,
      verify: false,
      isTrustedCheckout: false,
    }
  }
})

export type PersistentMintWithheldReason = 'restrictedStateRequires2fa' | 'ineligibleAccount' | 'other'

const WITHHELD_REASON_BY_WIRE_VALUE: Partial<Record<string, PersistentMintWithheldReason>> = {
  'restricted-state-requires-2fa': 'restrictedStateRequires2fa',
  'ineligible-account': 'ineligibleAccount',
}

export interface PersistentMintResult {
  minted: boolean
  withheld: boolean
  withheldReason?: PersistentMintWithheldReason
}

interface PersistentMintResponse {
  minted?: boolean
  reason?: string | null
}

const parsePersistentMintResponse = (data?: PersistentMintResponse): PersistentMintResult =>
  data?.minted === false
    ? { minted: false, withheld: true, withheldReason: WITHHELD_REASON_BY_WIRE_VALUE[data.reason ?? ''] ?? 'other' }
    : { minted: true, withheld: false }

export const newPersistentSession: PortalThunk<void, PersistentMintResult> = portalAction('consumer/newPersistentSession', async (_, thunk) => {
  try {
    const data: PersistentMintResponse | undefined = await thunk.extra.portalApi().post('/portal/consumers/persistent-login/new', {})
    return parsePersistentMintResponse(data)
  } catch {
    return { minted: false, withheld: false }
  }
})

export const revokePersistentSession: PortalThunk<void, boolean> = portalAction('consumer/revokePersistentSession', async (_, thunk) => {
  try {
    await thunk.extra.portalApi().post('/portal/consumers/persistent-login/revoke', {})
    return true
  } catch (e) {
    return false
  }
})

export const sendResetLink: PortalThunk<SendResetLinkRequest, void> = portalAction('sendResetLink', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/force-password-reset/send-email', request)
})

export const get2faCode: PortalThunk<{ sendBy: VerificationMethod }, void> = portalAction('2fa/getCode', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/auth/2fa/code', request)
})

export interface RiskExtra {
  orderToken: string
  checkpoint: 'CHECKOUT_CONFIRM'
  topazTraceId: string
  paymentMethodId?: string
  paymentMethodType?: PaymentMethodType
}
export const check2faCode: PortalThunk<
  {
    code: string
    riskExtra?: RiskExtra
  },
  void
> = portalAction('2fa/checkCode', async (request, thunk) => {
  const profilingSessionId = profilingIdSelector(thunk.getState())
  return await thunk.extra.portalApi().post('/portal/consumers/auth/2fa/check-code', {
    ...request,
    profilingSessionId,
  })
})

export const check2faDob: PortalThunk<{ mobile: string; dateOfBirth: Date; lastName: string }, void> = portalAction('2fa/check-dob', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/auth/2fa/check-dob', request)
})

export const sendOneTimePassword: PortalThunk<void, void> = portalAction('otp/getCode', async (request, thunk) => {
  const { email, mobile } = consumerSelector(thunk.getState())
  const { isMobileLogin } = mobileLoginSelector(thunk.getState())

  try {
    if (isMobileLogin) {
      await thunk.extra.portalApi().post('/portal/consumers/auth/otp/mobile/send', { mobile })
    } else {
      await thunk.extra.portalApi().post('/portal/consumers/auth/otp/send', { email })
    }
  } catch (error) {
    if (error.response.status === 429) {
      error.response.data = { errorCode: ErrorCode.TooManyOTPRequests }
    }
    throw error
  }
})

export const loginWithOneTimePassword: PortalThunk<string, AuthLoginResponse> = portalAction('otp/checkCode', async (request, thunk) => {
  const transactionToken = tokenSelector(thunk.getState())
  const { email, mobile } = consumerSelector(thunk.getState())
  const { isMobileLogin } = mobileLoginSelector(thunk.getState())

  // @ts-expect-error: OPERATION BLEED STOPPER
  const formData = new URLSearchParams({
    username: isMobileLogin ? mobile : email,
    'one-time-password': request,
  })
  return await thunk.extra.portalApi().post('/portal/consumers/auth/otp/login', formData)
})

export const requestTokenFromApp: PortalThunk<void, CheckoutTokenResponse> = portalAction('consumer/requestToken', async () => {
  if (window.xprops != null && window.xprops.canHandleAfterpayTokenRequest === true && isCallable(window.xprops.postMessageToInAppBrowser)) {
    return await new Promise((resolve, reject) => {
      const errorTimer = window.setTimeout(() => {
        window.removeEventListener('message', listener)
        reject(new Error())
      }, 2000)

      const listener: (event: MessageEvent) => void = (event) => {
        try {
          const data: CheckoutTokenResponse = JSON.parse(event.data)
          if (data.type === AFTERPAY_CHECKOUT_LOGIN_TOKEN || data.type === CASH_CHECKOUT_TOKEN) {
            window.clearTimeout(errorTimer)
            window.removeEventListener('message', listener)
            resolve(data)
          }
        } catch (err) {}
      }
      window.addEventListener('message', listener)
      // @ts-expect-error: OPERATION BLEED STOPPER
      window.xprops.postMessageToInAppBrowser(JSON.stringify({ type: CHECKOUT_TOKEN_REQUEST, payload: window.location.origin }))
    })
  }
  return await Promise.reject(new Error())
})

const isJWTExpired = (jwt: string): boolean => {
  try {
    if (typeof jwt !== 'string') {
      return true
    }
    const { exp }: any = jwtDecode(jwt)
    if (exp == null) {
      return true
    }
    const currentTime = new Date().getTime() / 1000
    if (currentTime > exp) {
      return true
    }
    return false
  } catch {
    return true
  }
}

export const getCashCheckoutToken = createAsyncThunk<string | null, void, PortalThunkConfig>('consumer/get/cashToken', async (_, thunkApi) => {
  let cashCheckoutToken = cashCheckoutTokenSelector(thunkApi.getState())
  if (cashCheckoutToken != null && isJWTExpired(cashCheckoutToken)) {
    await thunkApi.dispatch(requestTokenFromApp())
    cashCheckoutToken = cashCheckoutTokenSelector(thunkApi.getState())
  }
  return cashCheckoutToken
})

export const getEfaCode: PortalThunk<void, { phoneNumber: string; efaCheckStatus: EFA_CHECK_STATUS }> = portalAction('efa/getCode', async (request, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/efa-sms', request)
})

export const verifyEfaCode: PortalThunk<
  {
    code: string
  },
  void
> = portalAction('efa/verifyCode', async ({ code }, thunk) => {
  return await thunk.extra.portalApi().post('/portal/consumers/efa-verify-code', {
    code,
  })
})
